class MCartPromoBar extends HTMLElement {
  constructor() {
    super();
    this.progressBar = this.querySelector('.m-cart-promo-bar__bar-fill');
    this.messageElement = this.querySelector('.m-cart-promo-bar__message');
    this.lockIcon = this.querySelector('.m-cart-promo-bar__lock-icon');
    this.barType = this.dataset.barType || 'free_shipping';
    this.giftVariantId = parseInt(this.dataset.giftVariantId, 10) || null;
    // Barras presas a uma colecao (brinde e voucher) so contam os produtos dela
    this.restrictToEligible = this.dataset.restrictToEligible === 'true';
    this.hasEligibleItem = this.dataset.hasEligibleItem === 'true';
    this.eligibleProductIds = this.parseProductIds(this.dataset.eligibleProductIds);
    this.knownProductIds = this.parseProductIds(this.dataset.knownProductIds);
    this.pendingUpdate = false;
    this.priceBasis = this.dataset.priceBasis || 'final';
    this.thresholdType = this.dataset.thresholdType || 'value';
    this.cartTotal = parseFloat(this.dataset.cartTotal) || 0;
    this.cartItemCount = parseInt(this.dataset.cartItemCount, 10) || 0;
    this.threshold = this.thresholdType === 'quantity'
      ? parseInt(this.dataset.threshold, 10) || 3
      : parseFloat(this.dataset.threshold) || 350;
    this.barStyle = this.dataset.style || 'default';
    this.msgSuccess = this.dataset.msgSuccess || '';
    this.msgRemaining = this.dataset.msgRemaining || '';
    this.isUpdating = false;
    this.isSyncingGift = false;
    this.cartUpdateUnsubscriber = null;
    this.openCartDrawerUnsubscriber = null;
    this.documentListeners = [];
    this.observers = [];
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.init();
  }

  init() {
    this.updateProgress();
    this.listenForCartUpdates();
    if (this.isGiftBar()) {
      setTimeout(() => this.updateCartTotal(), 100);
      setTimeout(() => this.updateCartTotal(), 350);
    }
  }

  disconnectedCallback() {
    if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
    if (typeof this.openCartDrawerUnsubscriber === 'function') this.openCartDrawerUnsubscriber();

    this.documentListeners.forEach(({ eventName, handler }) => {
      document.removeEventListener(eventName, handler);
    });
    this.observers.forEach((observer) => observer.disconnect());

    this.documentListeners = [];
    this.observers = [];
    this.cartUpdateUnsubscriber = null;
    this.openCartDrawerUnsubscriber = null;
  }

  updateProgress() {
    const isQuantity = this.thresholdType === 'quantity';
    const current = isQuantity ? this.cartItemCount : this.cartTotal;
    const progressPercentage = Math.min((current / this.threshold) * 100, 100);
    const remainingAmount = Math.max(this.threshold - current, 0);
    const isGoalReached = this.isGoalReached();

    // Atualizar a barra de progresso
    if (this.progressBar) {
      this.progressBar.style.setProperty('--progress-percentage', `${progressPercentage}%`);
    }

    // Atualizar a mensagem apenas se mudou
    if (this.messageElement) {
      const newMessage = isGoalReached
        ? this.getLocalizedMessage('success')
        : this.getLocalizedMessage('remaining', remainingAmount);

      const currentMessage = this.messageElement.textContent.trim();

      // Só atualiza se a mensagem realmente mudou
      if (currentMessage !== newMessage) {
        // Remover <br> se o estilo for inline
        let processedMessage = newMessage;
        if (this.barStyle === 'inline') {
          processedMessage = newMessage.replace(/<br\s*\/?>/gi, ' ');
        }
        
        if (isGoalReached) {
          this.messageElement.innerHTML = `
            <span class="m-cart-promo-bar__success">
              ${processedMessage}
            </span>
          `;
        } else {
          this.messageElement.innerHTML = `
            <span class="m-cart-promo-bar__remaining">
              ${processedMessage}
            </span>
          `;
        }
      }
    }

    // Mostrar/ocultar a barra baseado no carrinho
    const isAvailable = !this.restrictToEligible || this.hasEligibleItem;
    const shouldShow = isAvailable && (this.thresholdType === 'quantity' ? this.cartItemCount > 0 : this.cartTotal > 0);
    const isCurrentlyVisible = this.style.display !== 'none';

    if (shouldShow !== isCurrentlyVisible) {
      this.style.display = shouldShow ? 'block' : 'none';
    }

    // Esconder cadeado quando atingir o valor (estilo inline)
    if (this.barStyle === 'inline' && this.lockIcon) {
      if (isGoalReached) {
        this.lockIcon.hidden = true;
      } else {
        this.lockIcon.hidden = false;
      }
    }
  }

  getLocalizedMessage(type, amount = 0) {
    // Tentar usar as mensagens customizadas do schema primeiro
    if (type === 'success' && this.msgSuccess) {
      let message = this.msgSuccess;
      if (this.barStyle === 'inline') {
        message = message.replace(/<br\s*\/?>/gi, ' ');
      }
      return message;
    } else if (type === 'remaining' && this.msgRemaining) {
      const formattedAmount = this.thresholdType === 'quantity'
        ? (amount === 1 ? '1 item' : `${amount} itens`)
        : this.formatCurrency(amount);
      let message = this.msgRemaining.replace('{{ amount }}', formattedAmount);
      if (this.barStyle === 'inline') {
        message = message.replace(/<br\s*\/?>/gi, ' ');
      }
      return message;
    }

    // Fallback para mensagens hardcoded
    const locale = document.documentElement.lang || 'en';

    const isGiftBar = this.barType.indexOf('gift') === 0;

    if (type === 'success') {
      if (isGiftBar) {
        return this.barType === 'gift_2'
          ? 'Parabéns! Você desbloqueou o Brinde 2'
          : 'Parabéns! Você desbloqueou o Brinde 1';
      }
      return locale === 'pt-BR'
        ? 'Parabéns! Você ganhou Frete Grátis'
        : 'Congratulations! You\'ve earned Free Shipping';
    } else {
      const formattedAmount = this.thresholdType === 'quantity'
        ? (amount === 1 ? '1 item' : `${amount} itens`)
        : this.formatCurrency(amount, locale);
      let message = locale === 'pt-BR'
        ? `Faltam ${formattedAmount} para você ganhar o Frete Grátis`
        : `Add ${formattedAmount} more to get free shipping`;

      if (isGiftBar) {
        const giftLabel = this.barType === 'gift_2' ? 'Brinde 2' : 'Brinde 1';
        message = `Faltam ${formattedAmount} para desbloquear o ${giftLabel}`;
      }
      
      // Remover <br> se o estilo for inline
      if (this.barStyle === 'inline') {
        return message.replace(/<br\s*\/?>/gi, ' ');
      }
      return message;
    }
  }

  formatCurrency(amount, locale = null) {
    if (!locale) {
      locale = document.documentElement.lang || 'en';
    }

    if (locale === 'pt-BR') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
  }

  isGiftBar() {
    return this.barType.indexOf('gift') === 0 && this.giftVariantId;
  }

  isGoalReached() {
    if (this.restrictToEligible && !this.hasEligibleItem) return false;
    const current = this.thresholdType === 'quantity' ? this.cartItemCount : this.cartTotal;
    return current >= this.threshold;
  }

  parseProductIds(value) {
    const ids = (value || '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !Number.isNaN(id));

    return new Set(ids);
  }

  // A resposta de /cart/add.js traz so os itens adicionados, nao o carrinho.
  // Calcular a meta com ela escondia as barras depois do "Comprar".
  isFullCart(cart) {
    return !!cart && Array.isArray(cart.items) && typeof cart.item_count === 'number' && typeof cart.total_price === 'number';
  }

  // Produto que entrou depois do ultimo render: so o servidor sabe se ele e da
  // colecao elegivel.
  hasUnclassifiedItems(cart) {
    return cart.items.some((item) => {
      const properties = item.properties || {};
      return properties._is_gift !== 'true' && !this.knownProductIds.has(Number(item.product_id));
    });
  }

  getEligibleCartState(cart) {
    // Barras de brinde e voucher: a meta conta APENAS itens da colecao elegivel —
    // mesma regra do desconto automatico da Shopify. Antes, o total do carrinho
    // inteiro destravava o brinde e a bolsa era cobrada quando o desconto nao
    // aplicava (pedido #2205), e o voucher de Lancamentos seguia "ganho" so com
    // produtos do Bazar. A lista vem do render da secao, refeito a cada alteracao
    // do carrinho; produto fora dela nao conta, mesmo com a lista vazia.
    // Voucher soma o preco original: o minimo do desconto e checado antes dos
    // descontos, e o proprio voucher e rateado nas linhas.
    const useOriginalPrice = this.priceBasis === 'original';

    const state = cart.items.reduce((acc, item) => {
      const properties = item.properties || {};
      if (properties._is_gift === 'true') return acc;
      if (this.restrictToEligible && !this.eligibleProductIds.has(Number(item.product_id))) return acc;

      let linePrice = typeof item.final_line_price === 'number'
        ? item.final_line_price
        : (typeof item.line_price === 'number' ? item.line_price : 0);
      if (useOriginalPrice && typeof item.original_line_price === 'number') {
        linePrice = item.original_line_price;
      }

      // Soma em centavos: somar reais quebrados dava 698.9999 numa meta de 699
      acc.cents += linePrice;
      acc.itemCount += item.quantity || 0;
      return acc;
    }, { cents: 0, itemCount: 0 });

    return { total: state.cents / 100, itemCount: state.itemCount };
  }

  setCartState(cart) {
    if (!this.isConnected) return;
    if (!this.isFullCart(cart)) {
      this.updateCartTotal();
      return;
    }
    if (this.restrictToEligible && this.hasUnclassifiedItems(cart)) {
      this.refreshFromSection(cart);
      return;
    }

    const eligibleState = this.getEligibleCartState(cart);
    this.cartTotal = eligibleState.total;
    this.cartItemCount = eligibleState.itemCount;
    if (this.restrictToEligible) {
      this.hasEligibleItem = this.hasEligibleCollectionItem(cart);
    }
    this.updateProgress();
    this.syncGiftLine(cart);
  }

  // Copia os valores da barra renderizada agora pelo servidor. Barras da mesma
  // secao dividem uma unica requisicao.
  async refreshFromSection(cart) {
    const sectionName = this.closest('#MinimogCartDrawer') ? 'cart-drawer' : 'cart-template';
    const requests = MCartPromoBar.sectionRequests;
    if (!requests[sectionName]) {
      requests[sectionName] = fetch(`${window.Shopify.routes.root}?sections=${sectionName}`)
        .then((response) => response.json())
        .then((sections) => new DOMParser().parseFromString(sections[sectionName] || '', 'text/html'))
        .finally(() => {
          delete requests[sectionName];
        });
    }
    const request = requests[sectionName];

    try {
      const html = await request;
      if (!this.isConnected) return;

      const freshBar = html.querySelector(`m-cart-promo-bar[data-bar-type="${this.barType}"]`);
      if (!freshBar) {
        this.style.display = 'none';
        return;
      }

      const { dataset } = freshBar;
      this.cartTotal = parseFloat(dataset.cartTotal) || 0;
      this.cartItemCount = parseInt(dataset.cartItemCount, 10) || 0;
      this.hasEligibleItem = dataset.hasEligibleItem === 'true';
      this.eligibleProductIds = this.parseProductIds(dataset.eligibleProductIds);
      this.knownProductIds = this.parseProductIds(dataset.knownProductIds);
      this.updateProgress();
      this.syncGiftLine(cart);
    } catch (error) {
      console.error('Erro ao atualizar barra do carrinho:', error);
    }
  }

  getGiftLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => {
      const properties = item.properties || {};
      return properties._is_gift === 'true';
    });
  }

  findGiftLine(cart, tier = this.barType) {
    return this.findGiftLines(cart, tier)[0];
  }

  findGiftLines(cart, tier = this.barType) {
    return this.getGiftLines(cart).filter((item) => {
      const properties = item.properties || {};
      return Number(item.variant_id) === this.giftVariantId && properties._gift_tier === tier;
    });
  }

  hasAnyGiftLineForVariant(cart) {
    return this.getGiftLines(cart).some((item) => Number(item.variant_id) === this.giftVariantId);
  }

  hasEligibleCollectionItem(cart) {
    if (!cart || !Array.isArray(cart.items) || this.eligibleProductIds.size === 0) return false;

    return cart.items.some((item) => {
      const properties = item.properties || {};
      return properties._is_gift !== 'true' && this.eligibleProductIds.has(Number(item.product_id));
    });
  }

  hasPurchasableItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;

    return cart.items.some((item) => {
      const properties = item.properties || {};
      return properties._is_gift !== 'true';
    });
  }

  async removeGiftLines(giftLines) {
    let updatedCart = null;

    for (const giftLine of giftLines) {
      updatedCart = await this.changeGiftLineQuantity(giftLine.key, 0);
    }

    return updatedCart;
  }

  getSectionsToBundle() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );
    return sectionsToBundle;
  }

  async fetchCartWithSections(sectionResponse = {}) {
    const cart = await fetch(`${window.Shopify.routes.root}cart.js`, { ...fetchConfig() }).then((response) => response.json());
    if (sectionResponse.sections) cart.sections = sectionResponse.sections;
    return cart;
  }

  emitCartUpdate(cart) {
    // Be Soft (Minimog 6): o contador do carrinho le cart.sections direto e
    // quebra sem elas. Carrinho sem sections pede o refresh do cart.js, que
    // busca as secoes sozinho.
    if (!cart || !cart.sections) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart });
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  async addGiftLine() {
    const sections = this.getSectionsToBundle();
    const { routes } = window.MinimogSettings;
    const body = JSON.stringify({
      items: [
        {
          id: this.giftVariantId,
          quantity: 1,
          properties: {
            _is_gift: 'true',
            _gift_source: 'cart_goal',
            _gift_tier: this.barType,
            Tipo: '[BRINDE]'
          }
        }
      ],
      sections,
      sections_url: window.location.pathname
    });

    const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } }).then((res) => res.json());
    // O /cart/add recusa com { errors } (ex.: brinde esgotado), nao com { status }
    if (response.status || response.errors) {
      throw new Error(response.description || response.errors || response.message || 'Gift add failed');
    }
    return this.fetchCartWithSections(response);
  }

  async changeGiftLineQuantity(lineKey, quantity) {
    const sections = this.getSectionsToBundle();
    const { routes } = window.MinimogSettings;
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections,
      sections_url: window.location.pathname
    });

    const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } }).then((res) => res.json());
    if (response.errors) throw new Error(response.errors);
    return response;
  }

  async syncGiftLine(cart) {
    if (!this.isConnected || !this.isGiftBar() || !cart || !Array.isArray(cart.items) || this.isSyncingGift) return;
    if (MCartPromoBar.giftSyncInProgress) return;

    const giftLine = this.findGiftLine(cart);
    const duplicateGiftLines = this.findGiftLines(cart).slice(1);
    const hasPurchasableItems = this.hasPurchasableItems(cart);
    const shouldHaveGift = hasPurchasableItems && this.hasEligibleItem && this.isGoalReached();

    this.updateProgress();

    if (!hasPurchasableItems) {
      const giftLines = this.getGiftLines(cart);
      if (giftLines.length === 0) return;

      this.isSyncingGift = true;
      MCartPromoBar.giftSyncInProgress = true;

      try {
        let updatedCart = await this.removeGiftLines(giftLines);
        if (updatedCart && !updatedCart.sections) {
          updatedCart = await this.fetchCartWithSections(updatedCart);
        }
        if (updatedCart) this.emitCartUpdate(updatedCart);
      } catch (error) {
        console.error('Erro ao remover brindes inválidos do carrinho:', error);
      } finally {
        this.isSyncingGift = false;
        MCartPromoBar.giftSyncInProgress = false;
      }
      return;
    }

    if (shouldHaveGift && giftLine && giftLine.quantity === 1 && duplicateGiftLines.length === 0) return;
    if (!shouldHaveGift && !giftLine) return;
    if (shouldHaveGift && !giftLine && this.hasAnyGiftLineForVariant(cart)) return;

    this.isSyncingGift = true;
    MCartPromoBar.giftSyncInProgress = true;

    try {
      let updatedCart = cart;
      if (shouldHaveGift) {
        if (giftLine && giftLine.quantity !== 1) {
          updatedCart = await this.changeGiftLineQuantity(giftLine.key, 1);
        } else {
          updatedCart = await this.addGiftLine();
        }
      } else {
        if (giftLine) {
          updatedCart = await this.changeGiftLineQuantity(giftLine.key, 0);
        }
      }

      if (duplicateGiftLines.length > 0) {
        updatedCart = await this.removeGiftLines(duplicateGiftLines);
      }

      if (!updatedCart.sections) {
        updatedCart = await this.fetchCartWithSections(updatedCart);
      }

      const updatedGiftLine = this.findGiftLine(updatedCart);
      if (!shouldHaveGift && updatedGiftLine) {
        updatedCart = await this.changeGiftLineQuantity(updatedGiftLine.key, 0);
        if (!updatedCart.sections) {
          updatedCart = await this.fetchCartWithSections(updatedCart);
        }
      }

      this.emitCartUpdate(updatedCart);
    } catch (error) {
      console.error('Erro ao sincronizar brinde do carrinho:', error);
    } finally {
      this.isSyncingGift = false;
      MCartPromoBar.giftSyncInProgress = false;
    }
  }

  listenForCartUpdates() {
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      this.cartUpdateUnsubscriber = window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, (cartData) => {
        if (this.isGiftBar()) return;

        const cart = cartData && cartData.cart ? cartData.cart : cartData;
        if (cart) this.setCartState(cart);
      });

      // Abrir a gaveta sempre confere o carrinho real, em todas as barras
      this.openCartDrawerUnsubscriber = window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.openCartDrawer, () => {
        setTimeout(() => {
          this.updateCartTotal();
        }, 300);
      });
    }

    const cartUpdatedHandler = (event) => {
      if (event.detail && event.detail.cart) {
        this.setCartState(event.detail.cart);
      }
    };
    document.addEventListener('cart:updated', cartUpdatedHandler);
    this.documentListeners.push({ eventName: 'cart:updated', handler: cartUpdatedHandler });
  }

  updateCartTotal() {
    if (!this.isConnected) return;
    // Pedido no meio de outro: refaz ao terminar para nao ficar com carrinho velho
    if (this.isUpdating) {
      this.pendingUpdate = true;
      return;
    }
    this.isUpdating = true;

    fetch(`${window.Shopify.routes.root}cart.js`)
      .then(response => response.json())
      .then(cart => {
        if (this.isFullCart(cart)) this.setCartState(cart);
      })
      .catch(error => {
        console.error('Erro ao atualizar total do carrinho:', error);
      })
      .finally(() => {
        this.isUpdating = false;
        if (this.pendingUpdate) {
          this.pendingUpdate = false;
          this.updateCartTotal();
        }
      });
  }
}

MCartPromoBar.giftSyncInProgress = false;
MCartPromoBar.sectionRequests = {};
customElements.define('m-cart-promo-bar', MCartPromoBar);

// Brinde que sobrou na sacola de quando a barra estava ligada. Com a barra
// desligada (ou o brinde esgotado) nada mais o tirava, e ele ia de graca no
// pedido. O cart-promo-bar.liquid lista as linhas e elas saem aqui; a sacola
// e redesenhada pelo cart:refresh do cart.js.
class MCartGiftCleanup extends HTMLElement {
  connectedCallback() {
    if (this.started || MCartGiftCleanup.running) return;
    const keys = (this.dataset.lineKeys || '').split(',').map((key) => key.trim()).filter(Boolean);
    if (keys.length === 0) return;
    this.started = true;
    MCartGiftCleanup.running = true;
    this.removeLines(keys).finally(() => {
      MCartGiftCleanup.running = false;
    });
  }

  async removeLines(keys) {
    try {
      for (const key of keys) {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        }).then((res) => res.json());
        if (response.errors || response.status) throw new Error(response.description || response.errors || response.message);
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      console.error('Erro ao tirar brinde antigo do carrinho:', error);
    }
  }
}

MCartGiftCleanup.running = false;
if (!customElements.get('m-cart-gift-cleanup')) customElements.define('m-cart-gift-cleanup', MCartGiftCleanup);
